import {Injectable} from '@angular/core';
import {Communication} from '../data/communication';
import {Mail} from '../data/mail';
import {CommunicationType} from '../data/communicationType';
import {AddCommunicationRequest} from '../data/requests/addCommunicationRequest';
import {RequestsWebsocketService} from '../../app-common/services/server/requests-websocket.service';
import {AppService} from '../../app-common/services/app/app.service';
import {EditCommunicationRequest} from '../data/requests/editCommunicationRequest';
import {
  CommunicationBuilderComponent
} from '../../communication-common/components/communication-builder/communication-builder.component';
import {FlagCommunicationRequest} from '../data/requests/flagCommunicationRequest';
import {PleaseWaitService} from '../../app-common/services/please-wait/please-wait.service';
import {EventEx} from '../../app-common/data/Event';
import {QueryCreator, QueryDTO, QueryFilter, QuerySort, QueryUnion} from '../../query/data/queryCreator';
import {Query} from '../../query/data/query';
import {CommunicationProperties} from '../../query/data/specifics/communicationProperties';
import {QueryableProperty} from '../../query/data/queryableProperty';
import {EqualsValuePropertyFilter} from '../../query/data/equalsValuePropertyFilter';
import {CommunicationsTabComponent} from '../section-tabs/communications-tab/communications-tab.component';
import {FlagService} from '../../flag/services/flag.service';
import {MailCheck} from '../data/mailCheck';
import {LeadDto} from '../data/requests/leadDto';
import {FullCommunicationDto} from '../data/fullCommunicationDto';
import {CommunicationDto} from '../data/communicationDto';
import {ApiService} from '../../app-common/services/server/api.service';
import {ConflictException} from '../../app-common/services/server/conflictException';
import {UserService} from '../../user/services/user/user.service';
import {CustomersService} from '../../customers/services/customers.service';
import {LoggerService} from '../../logging/services/logger.service';
import {LogLevel} from '../../logging/data/logLevel';
import {LogSourceType} from '../../logging/data/logSourceType';
import {LogSource} from '../../logging/data/logSource';
import {GenericVersion} from '../../audit/data/GenericVersion';
import {ContainsValuePropertyFilter} from "../../query/data/containsValuePropertyFilter";

@Injectable({
  providedIn: 'root'
})
export class CommunicationService {

  builders: Map<number, CommunicationBuilderComponent> = new Map<number, CommunicationBuilderComponent>();
  page: CommunicationsTabComponent;
  onConflict: EventEx = new EventEx();

  constructor(public userService: UserService,
              public customerService: CustomersService,
              public appService: AppService,
              public http: ApiService,
              public flagService: FlagService,
              public pleaseWaitService: PleaseWaitService,
              public websocket: RequestsWebsocketService,
              private loggerService: LoggerService) {
    this.userService = userService;
    this.appService = appService;
    this.websocket = websocket;
  }

  getDummyCommunication(): Communication {
    const dummy = new Communication();
    dummy.customerId = 0;
    dummy.personName = '';
    dummy.title = '';
    dummy.description = '';
    dummy.type = CommunicationType.Phone;
    dummy.outgoing = false;
    dummy.creationDate = new Date();
    dummy.deleted = false;
    dummy.createdBy = this.userService.currentUser.accountId;
    dummy.flagged = false;
    dummy.version = 1;
    dummy.eventDate = new Date();

    return dummy;
  }

  getDummyMail(): Mail {
    const dummy = new Mail();
    dummy.carrier = 'USPS';
    dummy.trackingNumber = '';
    dummy.returnReceipt = '';
    dummy.receivedOrSentDate = new Date();
    dummy.hasChecks = false;
    dummy.hasProblems = false;
    dummy.problems = '';
    dummy.fileSavedAs = '';
    dummy.filePath = '';
    dummy.problemReturnToSender = false;
    dummy.problemReturnMail = false;
    dummy.problemReturnUserId = this.userService.currentUser.accountId;
    dummy.problemReturnDate = new Date();
    dummy.problemCB = false;
    dummy.problemStipulations = false;
    dummy.problemHearing = false;
    dummy.problemRenewals = false;
    dummy.problemCNC = false;
    dummy.problemDenials = false;
    dummy.problemOutOfState = false;
    dummy.assigned = false;
    dummy.hasDateScanned = false;
    dummy.dateScanned = new Date();

    return dummy;
  }

  getDummyMailCheck() {
    const dummy = new MailCheck();
    dummy.amount = 0;
    dummy.check = '';
    dummy.payable = '';
    dummy.memo = '';
  }

  getDummyDto() {
    const dto = new CommunicationDto();
    dto.communication = this.getDummyCommunication();
    dto.mail = this.getDummyMail();
    dto.mailChecks = [];
    return dto;
  }

  async addLeadCommunication(leadDto: LeadDto, communication: Communication, mail: Mail, mailChecks: MailCheck[]): Promise<boolean> {
    communication = this.trimCommunicationNames(communication);
    mail = this.trimMailNames(mail);
    leadDto = this.trimLeadNames(leadDto);
    const request = new AddCommunicationRequest();
    this.pleaseWaitService.addToQueue();
    request.newLead = true;
    request.lead = leadDto;
    request.communication = communication;
    request.mail = mail;
    request.mailChecks = mailChecks;

    if (!request.communication.creationDate) {
      request.communication.creationDate = new Date();
    }

    try {
      await this.http.applicationApiClient.post('Communication', request);
      this.appService.showSuccessMessage('Communication Added', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: leadDto,
        message: 'Add lead communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: ex,
        message: 'Add lead communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async addCustomerCommunication(communication: Communication, mail: Mail, mailChecks: MailCheck[]): Promise<boolean> {
    communication = this.trimCommunicationNames(communication);
    mail = this.trimMailNames(mail);
    const request = new AddCommunicationRequest();
    this.pleaseWaitService.addToQueue();
    request.newLead = false;
    request.communication = communication;
    request.mail = mail;
    request.mailChecks = mailChecks;
    if (!request.communication.creationDate) {
      request.communication.creationDate = new Date();
    }

    try {
      await this.http.applicationApiClient.post('Communication', request);
      this.appService.showSuccessMessage('Communication Added', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: communication,
        message: 'Add contact communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: ex,
        message: 'Add contact communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }


  async editCommunication(communication: Communication, mail: Mail, mailChecks: MailCheck[], force: boolean = false): Promise<CommunicationDto | undefined> {
    communication = this.trimCommunicationNames(communication);
    mail = this.trimMailNames(mail);
    const request = new EditCommunicationRequest();
    this.pleaseWaitService.addToQueue();
    request.newLead = false;
    request.communication = communication;
    request.mail = mail;
    request.mailChecks = mailChecks;
    request.force = force;
    if (!request.communication.creationDate) {
      request.communication.creationDate = new Date();
    }

    if (request.communication.type !== CommunicationType.Mail) {
      request.mail = null;
      request.mailChecks = null;
    }

    try {
      const response = await this.http.applicationApiClient.put<CommunicationDto>('Communication', communication.id, request);
      this.appService.showSuccessMessage('Communication Edited', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: communication,
        message: 'Edit communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return response;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: ex,
        message: 'Edit communication'
      });
      if (ex instanceof ConflictException) {
        const myCommunication = new CommunicationDto();
        myCommunication.communication = communication;
        myCommunication.mail = mail;
        myCommunication.mailChecks = mailChecks;
        const myFullDto = new FullCommunicationDto();
        myFullDto.communicationDto = myCommunication;
        myFullDto.customerDto = await this.customerService.getCustomer(communication.customerId);
        myFullDto.creatorDto = await this.userService.getUser(communication.createdBy);
        myFullDto.modifierDto = await this.userService.getUser(communication.modifiedBy);
        this.onConflict.emit({my: myFullDto, db: ex.conflict});
      }
      return undefined;
    }
  }

  async revertCommunication(communicationId: number, version: number): Promise<boolean> {
    let response;
    try {
      response = await this.http.applicationApiClient.get(`Communication/History/${communicationId}/${version}/Revert`);
      this.appService.showSuccessMessage('Contact Edited', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: response,
        message: 'Revert communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      if (ex instanceof ConflictException) {
        const myCommunication = new CommunicationDto();
        myCommunication.communication = response.communication;
        myCommunication.mail = response.mail;
        myCommunication.mailChecks = response.mailChecks;
        const myFullDto = new FullCommunicationDto();
        myFullDto.communicationDto = myCommunication;
        myFullDto.customerDto = await this.customerService.getCustomer(response.customerId);
        myFullDto.creatorDto = await this.userService.getUser(response.createdBy);
        myFullDto.modifierDto = await this.userService.getUser(response.modifiedBy);
        this.onConflict.emit({my: myFullDto, db: ex.conflict});
      }
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: ex,
        message: 'Revert communication encountered an exception'
      });
      return false;
    }
  }

  async setFlagCommunication(communicationId: number, flagged: boolean, flagColor): Promise<boolean> {
    const request = new FlagCommunicationRequest();
    this.pleaseWaitService.addToQueue();
    request.communicationId = communicationId;
    request.flagged = flagged;
    request.flagColor = flagColor;

    try {
      await this.http.applicationApiClient.patch('Communication', communicationId, request);
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: communicationId,
        message: 'Set flag communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: ex,
        message: 'Set flag communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async deleteCommunication(id: number): Promise<boolean> {
    this.pleaseWaitService.addToQueue();
    try {
      await this.http.applicationApiClient.delete('Communication', id);
      this.appService.showSuccessMessage('Communication Deleted', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: id,
        message: 'Delete communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: ex,
        message: 'Delete communication'
      });
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async search(queryText, count: number, offset: number, customerId: number) {
    let dto;
    if (customerId){
      dto = QueryDTO.create(offset, count, [QueryUnion.create(
          QueryFilter.group([
            QueryFilter.singleValue('description', 'contains', queryText),
            QueryFilter.singleValue('customerId', 'eq', customerId.toString()),
            QueryFilter.singleValue('deleted', 'eq', 'false')
          ], 'and'))]
        , [QuerySort.create('description', 'asc')]);
    } else {
      dto = QueryDTO.create(offset, count, [QueryUnion.create(
          QueryFilter.group([
            QueryFilter.singleValue('description', 'contains', queryText),
            QueryFilter.singleValue('deleted', 'eq', 'false')
          ], 'and'))]
        , [QuerySort.create('description', 'asc')]);
    }

    const response = await this.http.applicationApiClient.getWithParams<FullCommunicationDto[]>('Communication/SearchWithAdditionalData', {filter: JSON.stringify(dto)});
    return response;
  }

  buildGetCommunicationsForCustomerQuery(customerId: number, entriesPerPage: number, pageNumber: number): QueryDTO {
    const customerProperties = new CommunicationProperties(this.flagService);
    const rootQuery = new Query();

    for (const property of customerProperties.properties) {
      if (property.name === 'customerId') {
        rootQuery.filters.push(this.createEqualsFilter(property, customerId));
      }
    }
    const sorting = new QuerySort();
    sorting.dir = customerProperties.sortingDescending ? 'desc' : 'asc';
    sorting.field = customerProperties.sortingPropertyName;

    return QueryCreator.generateFilter(rootQuery, sorting, entriesPerPage, pageNumber, 'and');
  }

  createEqualsFilter(property: QueryableProperty, value: any): EqualsValuePropertyFilter {
    const result = new EqualsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = value;
    return result;
  }


  async getCommunication(id: number, force: boolean = false): Promise<FullCommunicationDto> {
    if (id <= 0) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: id,
        message: 'Get communication by id <= 0'
      });
      return undefined;
    }
    const fullCommDto = await this.http.applicationApiClient.getWithParams<FullCommunicationDto>(`Communication/${id}/Full`, {force});
    if (fullCommDto) {
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.CommunicationService, {
        entity: id,
        message: 'Get communication by id'
      });
      return this.trimFullCommunicationDtoNames(fullCommDto);
    }
  }

  async getCommunicationHistory(communicationId: number, force: boolean = false): Promise<GenericVersion[]> {
    if (communicationId <= 0) {
      return undefined;
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.CommunicationService, 'Failed to fetch audits!');
    }
    return await this.http.applicationApiClient.getWithParams<GenericVersion[]>(`Communication/History/${communicationId}`, {force});
  }

  async getCommunicationVersion(communicationId: number, version: number, force: boolean = false): Promise<CommunicationDto> {
    if (communicationId <= 0 || version <= 0) {
      return undefined;
    }
    return await this.http.applicationApiClient.getWithParams<CommunicationDto>(`Communication/History/${communicationId}/${version}`, null);
  }

  async getFullCommunicationVersion(communicationId: number, version: number, force: boolean = false): Promise<FullCommunicationDto> {
    if (communicationId <= 0 || version <= 0) {
      return undefined;
    }
    return await this.http.applicationApiClient.getWithParams<FullCommunicationDto>(`Communication/History/${communicationId}/${version}/FullComm`, null);
  }

  async getCommunicationVersionLabels(communicationId: number, version: number, force: boolean = false): Promise<string[]> {
    if (communicationId <= 0 || version <= 0) {
      return undefined;
    }
    try {
      return await this.http.applicationApiClient.getWithParams<string[]>(`Communication/History/${communicationId}/${version}/Labels`, false);
    } catch (e) {
      return ['INITIAL'];
    }
  }

  trimLeadNames(lead: LeadDto): LeadDto {
    if (lead) {
      if (lead.name) {
        lead.name = lead.name.trim();
      }
      if (lead.companyName) {
        lead.companyName = lead.companyName.trim();
      }
      if (lead.email) {
        lead.email = lead.email.trim();
      }
      if (lead.phoneNumber) {
        lead.phoneNumber = lead.phoneNumber.trim();
      }
    }
    return lead;
  }

  trimFullCommunicationDtoNames(fullCommunicationDto: FullCommunicationDto): FullCommunicationDto {
    if (fullCommunicationDto) {
      if (fullCommunicationDto.communicationDto) {
        fullCommunicationDto.communicationDto = this.trimCommunicationDtoNames(fullCommunicationDto.communicationDto);
      }
      if (fullCommunicationDto.creatorDto) {
        fullCommunicationDto.creatorDto = this.userService.trimUserDtoNames(fullCommunicationDto.creatorDto);
      }
      if (fullCommunicationDto.customerDto && fullCommunicationDto.customerDto.customer) {
        fullCommunicationDto.customerDto.customer = this.customerService.trimCustomerNames(fullCommunicationDto.customerDto.customer);
      }
      if (fullCommunicationDto.modifierDto) {
        fullCommunicationDto.modifierDto = this.userService.trimUserDtoNames(fullCommunicationDto.modifierDto);
      }
    }
    return fullCommunicationDto;
  }

  trimCommunicationDtoNames(communicationDto: CommunicationDto): CommunicationDto {
    if (communicationDto) {
      if (communicationDto.communication) {
        communicationDto.communication = this.trimCommunicationNames(communicationDto.communication);
      }
      if (communicationDto.mail) {
        communicationDto.mail = this.trimMailNames(communicationDto.mail);
      }
    }
    return communicationDto;
  }

  trimMailNames(mail: Mail): Mail {
    if (mail && mail.modifiedBy) {
      mail.modifiedBy = mail.modifiedBy.trim();
    }
    if (mail && mail.createdBy) {
      mail.createdBy = mail.createdBy.trim();
    }
    return mail;
  }

  trimCommunicationNames(communication: Communication): Communication {
    if (communication && communication.personName) {
      communication.personName = communication.personName.trim();
    }
    if (communication && communication.modifiedBy) {
      communication.modifiedBy = communication.modifiedBy.trim();
    }
    if (communication && communication.createdBy) {
      communication.createdBy = communication.createdBy.trim();
    }
    if (communication && communication.title) {
      communication.title = communication.title.trim();
    }
    return communication;
  }
}
